import { useEffect } from 'react';
import { create } from 'zustand';
import { X } from 'lucide-react';
import { uid } from '../../lib/storage';

export interface ToastItem {
  id: string;
  title: string;
  body?: string;
  tone?: 'info' | 'success' | 'warn' | 'error';
  /** ms; 0 keeps it until dismissed. */
  duration?: number;
}

interface ToastState {
  items: ToastItem[];
  push: (toast: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
}

export const useToasts = create<ToastState>((set) => ({
  items: [],
  push: (toast) => {
    const id = uid('t');
    set((s) => ({ items: [...s.items.slice(-4), { id, duration: 4000, tone: 'info', ...toast }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

export function toast(title: string, options: Omit<ToastItem, 'id' | 'title'> = {}): string {
  return useToasts.getState().push({ title, ...options });
}

const TONE: Record<NonNullable<ToastItem['tone']>, string> = {
  info: 'border-l-accent',
  success: 'border-l-up',
  warn: 'border-l-warn',
  error: 'border-l-down',
};

function ToastView({ item }: { item: ToastItem }) {
  const dismiss = useToasts((s) => s.dismiss);
  useEffect(() => {
    if (!item.duration) return;
    const t = setTimeout(() => dismiss(item.id), item.duration);
    return () => clearTimeout(t);
  }, [item, dismiss]);
  return (
    <div
      role="status"
      className={`pointer-events-auto flex w-80 items-start gap-3 rounded-lg border border-line border-l-4 bg-raised px-4 py-3 shadow-pop ${TONE[item.tone ?? 'info']}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">{item.title}</p>
        {item.body && <p className="mt-0.5 text-[13px] text-fg-2">{item.body}</p>}
      </div>
      <button type="button" aria-label="Dismiss" onClick={() => dismiss(item.id)} className="text-fg-3 hover:text-fg">
        <X size={16} />
      </button>
    </div>
  );
}

export function Toaster() {
  const items = useToasts((s) => s.items);
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[1100] flex flex-col gap-2" aria-live="polite">
      {items.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </div>
  );
}
